import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminReviews.css';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterRating, setFilterRating] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/reviews`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReviews(data.data || []);
        } else {
          setError(data.message || 'Failed to fetch reviews');
        }
      } else {
        setError('Failed to fetch reviews');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError('Error loading reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async (reviewId, productName, userName) => {
    if (!window.confirm(`Are you sure you want to delete the review from ${userName} for "${productName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Review deleted successfully');
        fetchReviews(); // Refresh the list
      } else {
        alert(data.message || 'Failed to delete review');
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Error deleting review');
    }
  };

  const handleApproveReview = async (reviewId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Review approved successfully');
        fetchReviews(); // Refresh the list
      } else {
        alert(data.message || 'Failed to approve review');
      }
    } catch (error) {
      console.error('Error approving review:', error);
      alert('Error approving review');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={`star ${star <= rating ? 'filled' : 'empty'}`}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Filter reviews based on rating and search term
  const filteredReviews = reviews.filter(review => {
    const matchesRating = filterRating === 'all' || review.rating === parseInt(filterRating);
    const matchesSearch = searchTerm === '' || 
      review.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${review.first_name} ${review.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRating && matchesSearch;
  });

  // Calculate statistics
  const totalReviews = reviews.length;
  const averageRating = totalReviews > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / totalReviews).toFixed(1)
    : 0;
  const approvedCount = reviews.filter(r => r.is_approved === 1).length;
  const pendingCount = reviews.filter(r => r.is_approved === 0).length;

  if (loading) {
    return (
      <div className="admin-reviews-loading">
        <div className="spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-reviews-error">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-reviews">
      <div className="admin-reviews-header">
        <h1>Product Reviews Management</h1>
        
        {/* Statistics Cards */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-value">{totalReviews}</div>
            <div className="stat-label">Total Reviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{averageRating}</div>
            <div className="stat-label">Average Rating</div>
            <div className="stat-stars">{renderStars(Math.round(averageRating))}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{approvedCount}</div>
            <div className="stat-label">Approved</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{pendingCount}</div>
            <div className="stat-label">Pending</div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by product, customer, or review content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                ×
              </button>
            )}
          </div>
          
          <div className="rating-filter">
            <label>Filter by Rating:</label>
            <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
              <option value="all">All Ratings</option>
              <option value="5">★★★★★ (5)</option>
              <option value="4">★★★★☆ (4)</option>
              <option value="3">★★★☆☆ (3)</option>
              <option value="2">★★☆☆☆ (2)</option>
              <option value="1">★☆☆☆☆ (1)</option>
            </select>
          </div>
          
          <button className="refresh-btn" onClick={fetchReviews}>
            ⟳ Refresh
          </button>
        </div>
      </div>

      {/* Reviews Table */}
      <div className="reviews-table-container">
        {filteredReviews.length === 0 ? (
          <div className="no-data">
            {searchTerm || filterRating !== 'all' 
              ? 'No reviews match your filters.' 
              : 'No reviews found.'}
          </div>
        ) : (
          <table className="reviews-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Customer</th>
                <th>Rating</th>
                <th>Review</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReviews.map(review => (
                <tr key={review.id} className={review.is_approved === 0 ? 'pending-review' : ''}>
                  <td className="product-cell">
                    <div className="product-name">{review.product_name || 'Unknown Product'}</div>
                  </td>
                  <td className="customer-cell">
                    <div className="customer-name">
                      {review.first_name || ''} {review.last_name || ''}
                    </div>
                    <div className="customer-email">{review.email || 'No email'}</div>
                  </td>
                  <td className="rating-cell">
                    {renderStars(review.rating)}
                    <span className="rating-number">({review.rating})</span>
                  </td>
                  <td className="review-cell">
                    {review.title && (
                      <div className="review-title">{review.title}</div>
                    )}
                    <div className="review-comment">{review.comment || 'No comment'}</div>
                  </td>
                  <td className="date-cell">
                    {formatDate(review.created_at)}
                  </td>
                  <td className="status-cell">
                    <span className={`status-badge ${review.is_approved === 1 ? 'approved' : 'pending'}`}>
                      {review.is_approved === 1 ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    {review.is_approved === 0 && (
                      <button 
                        className="approve-btn"
                        onClick={() => handleApproveReview(review.id)}
                        title="Approve Review"
                      >
                        ✓
                      </button>
                    )}
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteReview(review.id, review.product_name, `${review.first_name} ${review.last_name}`)}
                      title="Delete Review"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer with count */}
      <div className="reviews-footer">
        <div className="reviews-count">
          Showing {filteredReviews.length} of {reviews.length} reviews
        </div>
      </div>
    </div>
  );
};

export default AdminReviews;